import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getPolicyPremiumTotal, getPolicyGrandTotal, getServicesTotal } from "../utils/policyCalc";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "../components/ui/sheet";
import { Plus, Pencil, Trash2, Search, FileText, X, User, RefreshCw, CheckCircle, RotateCcw, Pause, Play, Calendar, Filter, SlidersHorizontal, Download, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import VoiceInput from "../components/VoiceInput";
import DocumentsDialog from "../components/DocumentsDialog";
import DocumentDropzone from "../components/DocumentDropzone";
import DataPagination from "../components/DataPagination";
import { ExportColumnsDialog } from "../components/ExportColumnsDialog";
import { SortableHeader, sortData } from "../components/SortableHeader";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { DeleteConfirmDialog } from "../components/ui/confirm-dialog";

// Import reusable policy components
import { 
  SuspendPolicyDialog, 
  ReactivatePolicyDialog, 
  RenewalPolicyDialog,
  POLICY_TYPES,
  SUSPENDABLE_TYPES,
  NOT_RENEWED_REASONS,
  getPolicyTypeInfo
} from "../components/policies";
import { useConferma } from "../components/ConfirmProvider";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era formatDateSafe).
import { formatDate } from "../lib/format";

export default function PoliciesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const conferma = useConferma();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distingue "non c'e' nessuna polizza" da "il caricamento e' fallito":
  // senza questo un problema di collegamento sembrava archivio vuoto.
  const [loadError, setLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedPolicyForDocs, setSelectedPolicyForDocs] = useState(null);
  const [showOCR, setShowOCR] = useState(false);
  const pendingFileRef = useRef(null);
  const [activeTab, setActiveTab] = useState("all");
  
  // Advanced search filters
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tableSort, setTableSort] = useState({ key: "expiry_date", dir: "asc" });
  const [advancedFilters, setAdvancedFilters] = useState({
    company: "",
    policyTypes: [],
    status: "", // active, expired, suspended, renewed
    expiryDateFrom: "",
    expiryDateTo: "",
    premiumMin: "",
    premiumMax: "",
    licensePlate: "",
    paymentFrequency: "", // annual, semiannual
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  
  // Suspension dialog state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [undoingSemester, setUndoingSemester] = useState(false);
  const [selectedPolicyForAction, setSelectedPolicyForAction] = useState(null);
  const [suspensionData, setSuspensionData] = useState({
    suspension_reminder: "",
    suspension_reason: ""
  });
  const [reactivationData, setReactivationData] = useState({
    new_expiry_date: "",
    new_start_date: ""
  });
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPolicies, setTotalPolicies] = useState(0);
  const [storicoCount, setStoricoCount] = useState(0);
  const ITEMS_PER_PAGE = 25;
  
  // Renewal dialog state
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [selectedPolicyForRenewal, setSelectedPolicyForRenewal] = useState(null);
  // Dopo il rinnovo: nuova polizza creata, con la possibilità di allegare subito il documento
  const [renewalResult, setRenewalResult] = useState(null);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [saving, setSaving] = useState(false);
  const [renewalData, setRenewalData] = useState({
    renewalType: "same_company", // same_company, other_company, not_renewed
    newCompany: "",
    newPremium: "",
    newConsultationFee: "",
    createNewPolicy: true,
    notRenewedReason: "",
    notRenewedReasonOther: "",
    notes: ""
  });
  
  // Client search state
  const [clientSearch, setClientSearch] = useState("");
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientSearchRef = useRef(null);
  
  const [formData, setFormData] = useState({
    client_id: "",
    policy_number: "",
    company: "",
    policy_type: "",
    premium: "",
    consultation_fee: "",
    payment_frequency: "annual",
    start_date: "",
    expiry_date: "",
    semiannual_expiry: "",
    license_plate: "",
    notes: "",
    additional_services: []
  });

  // Calculate expiry_date (1 year) and semiannual_expiry (6 months) when start_date changes
  useEffect(() => {
    if (formData.start_date) {
      const startDate = new Date(formData.start_date);
      
      // Auto-calculate expiry date (1 year from start)
      const expiryDate = new Date(startDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      
      // Auto-calculate semiannual date (6 months from start) if semiannual
      if (formData.payment_frequency === "semiannual") {
        const semiannualDate = new Date(startDate);
        semiannualDate.setMonth(semiannualDate.getMonth() + 6);
        setFormData(prev => ({
          ...prev,
          expiry_date: expiryDate.toISOString().split('T')[0],
          semiannual_expiry: semiannualDate.toISOString().split('T')[0]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          expiry_date: expiryDate.toISOString().split('T')[0],
          semiannual_expiry: ""
        }));
      }
    }
  }, [formData.start_date, formData.payment_frequency]);

  const clientsLoadedRef = useRef(false);
  const initialLoadDone = useRef(false);

  // Single fetch function for policies (search-aware)
  const fetchPolicies = async (page = 1, search = "") => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const requests = [
        axios.get(`/api/policies?page=${page}&limit=${ITEMS_PER_PAGE}&storico=${activeTab === "storico" ? "true" : "false"}${search ? `&search=${encodeURIComponent(search)}` : ''}`)
      ];
      // Load clients only once on first fetch
      if (!clientsLoadedRef.current) {
        requests.push(axios.get('/api/clients?all=true'));
      }
      
      const results = await Promise.all(requests);
      const policiesRes = results[0];
      
      if (policiesRes.data.items) {
        setPolicies(policiesRes.data.items);
        setTotalPolicies(policiesRes.data.total);
        setTotalPages(policiesRes.data.pages);
        setCurrentPage(policiesRes.data.page);
        setStoricoCount(policiesRes.data.totale_storico || 0);
      } else {
        setPolicies(policiesRes.data);
      }
      
      if (results[1]) {
        setClients(results[1].data.items || results[1].data || []);
        clientsLoadedRef.current = true;
      }
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchPolicies(newPage, searchTerm);
    }
  };
  
  // Single useEffect for initial load + search with debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchPolicies(1, searchTerm);
    }, searchTerm ? 400 : 0);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, activeTab]);

  // Handle ?edit=policyId from navigation
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && policies.length > 0) {
      const policyToEdit = policies.find(p => p.policy_id === editId);
      if (policyToEdit) {
        handleEdit(policyToEdit);
        setSearchParams({}, { replace: true });
      } else {
        // Policy not on current page - fetch it directly
        axios.get(`/api/policies/${editId}`).then(res => {
          handleEdit(res.data);
          setSearchParams({}, { replace: true });
        }).catch(() => {
          toast.error("Polizza non trovata");
          setSearchParams({}, { replace: true });
        });
      }
    }
  }, [policies, searchParams]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target)) {
        setShowClientSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error("Seleziona un cliente");
      return;
    }
    
    try {
      const data = {
        ...formData,
        premium: parseFloat(formData.premium) || 0,
        consultation_fee: parseFloat(formData.consultation_fee) || 0,
        additional_services: (formData.additional_services || [])
          .filter(s => s.provider || s.description || s.cost)
          .map(s => ({ provider: s.provider || "", description: s.description || "", cost: parseFloat(s.cost) || 0 }))
      };

      let createdPolicyId = null;

      if (editingPolicy) {
        await axios.put(`/api/policies/${editingPolicy.policy_id}`, data);
        toast.success("Polizza aggiornata con successo");
      } else {
        const response = await axios.post('/api/policies', data);
        createdPolicyId = response.data?.policy_id;
        toast.success("Polizza creata con successo");
      }
      
      // Auto-upload the document used for data extraction
      if (createdPolicyId && pendingFileRef.current) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append("file", pendingFileRef.current);
          await axios.post(
            `/api/documents/upload?entity_type=policy&entity_id=${createdPolicyId}&storage=both&auto_rename=true`,
            formDataUpload,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          toast.success("Documento salvato nell'archivio della polizza");
        } catch (uploadErr) {
          console.error("Document auto-upload error:", uploadErr);
          toast.warning("Polizza creata ma il documento non è stato salvato nell'archivio. Puoi caricarlo manualmente.");
        }
        pendingFileRef.current = null;
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const dettaglio = error.response?.data?.detail;
      if (dettaglio) {
        toast.error(typeof dettaglio === "string" ? dettaglio : JSON.stringify(dettaglio));
      } else if (error.response) {
        toast.error(`Errore ${error.response.status} durante il salvataggio della polizza`);
      } else {
        toast.error("Nessuna risposta dal server: la polizza potrebbe essere stata salvata comunque. Ricontrolla l'elenco prima di ripetere.");
      }
    }
  };

  // Open delete confirmation dialog
  const confirmDeletePolicy = (policy) => {
    setPolicyToDelete(policy);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!policyToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/policies/${policyToDelete.policy_id}`);
      toast.success(`Polizza "${policyToDelete.policy_number}" eliminata con successo`);
      setDeleteConfirmOpen(false);
      setPolicyToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione della polizza");
    } finally {
      setDeleting(false);
    }
  };

  // Open renewal dialog
  const openRenewalDialog = (policy) => {
    setSelectedPolicyForRenewal(policy);
    setRenewalData({
      renewalType: "same_company",
      newCompany: "",
      newPremium: policy.premium?.toString() || "",
      newConsultationFee: policy.consultation_fee?.toString() || "",
      createNewPolicy: true,
      notRenewedReason: "",
      notRenewedReasonOther: "",
      notes: ""
    });
    setRenewalResult(null);
    setRenewalDialogOpen(true);
  };

  // Handle renewal submission
  const handleRenewal = async () => {
    if (!selectedPolicyForRenewal) return;
    
    setSaving(true);
    const policy = selectedPolicyForRenewal;
    
    try {
      // Handle semester renewal
      if (renewalData.renewalType === "renew_semester") {
        const response = await axios.post(`/api/policies/${policy.policy_id}/renew-semester`);
        if (response.data.semiannual_completed) {
          toast.success("Ultima rata semestrale pagata! Prossima scadenza: rinnovo annuale.");
        } else {
          toast.success("Rata semestrale rinnovata! (+6 mesi)");
        }
      } else if (renewalData.renewalType === "not_renewed") {
        // Mark as not renewed with reason
        if (!renewalData.notRenewedReason) {
          toast.error("Seleziona un motivo");
          return;
        }
        
        const reason = renewalData.notRenewedReason === "other" 
          ? renewalData.notRenewedReasonOther 
          : NOT_RENEWED_REASONS.find(r => r.value === renewalData.notRenewedReason)?.label;
        
        await axios.post(`/api/policies/${policy.policy_id}/mark-not-renewed`, {
          reason: reason,
          reason_code: renewalData.notRenewedReason,
          notes: renewalData.notes
        });
        
        toast.success("Polizza segnata come non rinnovata");
      } else {
        // v3.9.12 — UNA sola chiamata (come la scheda polizza): niente più
        // mark-renewed + renew-create in due passi non atomici.
        const newCompany = renewalData.renewalType === "other_company" 
          ? renewalData.newCompany.toUpperCase() 
          : "";

        if (renewalData.renewalType === "other_company" && !newCompany) {
          toast.error("Inserisci la nuova compagnia");
          return;
        }

        const newPremium = renewalData.newPremium !== "" && renewalData.newPremium != null
          ? parseFloat(renewalData.newPremium)
          : null;
        const newConsultationFee = renewalData.newConsultationFee !== "" && renewalData.newConsultationFee != null
          ? parseFloat(renewalData.newConsultationFee)
          : null;

        const res = await axios.post(`/api/policies/${policy.policy_id}/renew`, {
          new_company: newCompany || null,
          new_premium: Number.isNaN(newPremium) ? null : newPremium,
          new_consultation_fee: Number.isNaN(newConsultationFee) ? null : newConsultationFee,
          notes: renewalData.notes,
          create_new_policy: renewalData.createNewPolicy !== false,
          // 17/09/2026 — frequenza scelta per il NUOVO contratto (+ data prima rata se semestrale)
          new_payment_frequency: renewalData.newPaymentFrequency
            || (policy.payment_frequency === "semiannual" ? "semestral" : "annual"),
          new_semiannual_expiry: renewalData.newSemiannualExpiry || null,
        });

        if (res.data?.new_policy_id) {
          toast.success(
            renewalData.createNewPolicy !== false
              ? `Polizza rinnovata: creata ${res.data.new_policy_number || "la nuova polizza"}`
              : "Polizza segnata come rinnovata"
          );
        } else {
          toast.success("Polizza segnata come rinnovata");
        }

        if (res.data?.new_policy_id && renewalData.createNewPolicy !== false) {
          // resto aperto: c'è da allegare il documento della nuova polizza
          setRenewalResult(res.data);
          fetchData();
          setSaving(false);
          return;
        }
      }
      
      setRenewalDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il rinnovo");
    } finally {
      setSaving(false);
    }
  };

  // Allega il documento della polizza appena rinnovata (locale + MEGA)
  const allegaDocumentoRinnovo = async (file) => {
    if (!file || !renewalResult?.new_policy_id) return;
    setUploadingAttach(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `/api/documents/upload?entity_type=policy&entity_id=${renewalResult.new_policy_id}&storage=both&auto_rename=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Documento allegato alla nuova polizza");
      setRenewalResult(null);
      setRenewalDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Non riesco ad allegare il documento");
    } finally {
      setUploadingAttach(false);
    }
  };

  const saltaAllegato = () => {
    setRenewalResult(null);
    setRenewalDialogOpen(false);
    fetchData();
  };

  const handleUnmarkRenewed = async (policy) => {
    const policyId = policy?.policy_id || policy;
    if (!(await conferma({
      titolo: "Annullare il rinnovo di questa polizza?",
      descrizione:
        "La polizza torna in stato normale (attiva, o scaduta se la data è passata) e il flag rinnovo viene rimosso. " +
      "L'operazione è tracciata nello storico modifiche.",
    }))) return;

    // se esiste la polizza creata dal rinnovo, chiedi se eliminarla
    let deleteCreated = false;
    if (policy?.renewed_policy_id) {
      deleteCreated = await conferma({
        titolo: "Eliminare anche la polizza di rinnovo?",
        descrizione:
          "Il rinnovo ha creato automaticamente una seconda polizza. Confermando viene eliminata anche quella " +
          "(con i suoi documenti); annullando resta al suo posto.",
        conferma: "Elimina anche quella",
        annulla: "Lascia la polizza",
      });
    }

    try {
      const res = await axios.post(
        `/api/policies/${policyId}/unmark-renewed`,
        null,
        { params: { delete_created: deleteCreated } }
      );
      toast.success(
        res.data?.deleted_policy_id
          ? "Rinnovo annullato ed eliminata la polizza creata"
          : "Rinnovo annullato"
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento della polizza");
    }
  };

  // v3.9.12 — annulla "non rinnovata" (click sbagliato)
  const handleUndoNotRenewed = async (policyId) => {
    if (!(await conferma({
      titolo: "Annullare il \"non rinnovata\"?",
      descrizione:
        "La polizza torna in stato normale e ricompare nei report. L'operazione è tracciata nello storico modifiche.",
    }))) return;
    try {
      const res = await axios.post(`/api/policies/${policyId}/unmark-not-renewed`);
      toast.success(`Polizza ripristinata (stato: ${res.data?.status === "expired" ? "scaduta" : "attiva"})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel ripristino");
    }
  };

  // v3.9.12 — annulla il pagamento della rata semestrale (click sbagliato)
  const handleUndoSemester = async (policyId) => {
    if (!(await conferma({
      titolo: "Annullare il pagamento della rata semestrale?",
      descrizione: "La rata tornerà \"da pagare\" e la scadenza verrà riportata al periodo giusto. L'operazione viene registrata nello storico modifiche.",
    }))) return;

    setUndoingSemester(true);
    try {
      const res = await axios.post(`/api/policies/${policyId}/unmark-semester`);
      const rata = res.data?.rata_annullata;
      toast.success(
        rata?.period
          ? `Pagamento annullato. Rata del ${rata.period} di nuovo da pagare.`
          : "Pagamento rata annullato."
      );
      setRenewalDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'annullamento del pagamento");
    } finally {
      setUndoingSemester(false);
    }
  };

  // Suspension handlers
  const openSuspendDialog = (policy) => {
    setSelectedPolicyForAction(policy);
    setSuspensionData({ suspension_reminder: "", suspension_reason: "" });
    setSuspendDialogOpen(true);
  };

  const handleSuspend = async () => {
    if (!suspensionData.suspension_reminder) {
      toast.error("Inserisci una data promemoria");
      return;
    }
    
    try {
      await axios.post(`/api/policies/${selectedPolicyForAction.policy_id}/suspend`, suspensionData);
      toast.success("Polizza sospesa");
      setSuspendDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella sospensione");
    }
  };

  const openReactivateDialog = (policy) => {
    setSelectedPolicyForAction(policy);
    // 29/10/2026: la scadenza la compila la finestra (giorni residui dalla sospensione);
    // la data di inizio resta quella originale se non la scrivi
    setReactivationData({ new_expiry_date: "", new_start_date: "" });
    setReactivateDialogOpen(true);
  };

  const handleReactivate = async () => {
    try {
      // se la scadenza è vuota il CRM la calcola dai giorni congelati alla sospensione
      const { data } = await axios.post(`/api/policies/${selectedPolicyForAction.policy_id}/reactivate`, reactivationData);
      toast.success(`Polizza riattivata — nuova scadenza ${formatDate(data?.new_expiry_date, "-")}`);
      setReactivateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella riattivazione");
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    const client = clients.find(c => c.client_id === policy.client_id);
    setSelectedClient(client || null);
    setClientSearch(client ? client.name : "");
    setFormData({
      client_id: policy.client_id,
      policy_number: policy.policy_number,
      company: policy.company,
      policy_type: policy.policy_type,
      premium: policy.premium?.toString() || "",
      consultation_fee: policy.consultation_fee?.toString() || "0",
      payment_frequency: policy.payment_frequency || "annual",
      start_date: policy.start_date,
      expiry_date: policy.expiry_date,
      semiannual_expiry: policy.semiannual_expiry || "",
      license_plate: policy.license_plate || "",
      notes: policy.notes || "",
      additional_services: policy.additional_services || []
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPolicy(null);
    setSelectedClient(null);
    setClientSearch("");
    pendingFileRef.current = null;
    setFormData({
      client_id: "",
      policy_number: "",
      company: "",
      policy_type: "",
      premium: "",
      consultation_fee: "",
      payment_frequency: "annual",
      start_date: "",
      expiry_date: "",
      semiannual_expiry: "",
      license_plate: "",
      notes: "",
      additional_services: []
    });
  };

  const handleVoiceResult = async (transcript) => {
    try {
      const response = await axios.post('/api/ai/parse-policy', { text: transcript });
      if (response.data.parsed_data) {
        const parsed = response.data.parsed_data;
        
        // If client_id was parsed, find and set the client
        if (parsed.client_id) {
          const client = clients.find(c => c.client_id === parsed.client_id);
          if (client) {
            setSelectedClient(client);
            setClientSearch(client.name);
          }
        }
        
        setFormData(prev => ({
          ...prev,
          client_id: parsed.client_id || prev.client_id,
          policy_number: parsed.policy_number || prev.policy_number,
          company: parsed.company || prev.company,
          policy_type: parsed.policy_type || prev.policy_type,
          premium: parsed.premium?.toString() || prev.premium,
          consultation_fee: parsed.consultation_fee?.toString() || prev.consultation_fee,
          start_date: parsed.start_date || prev.start_date,
          expiry_date: parsed.expiry_date || prev.expiry_date,
          license_plate: parsed.license_plate || prev.license_plate,
          notes: parsed.notes || prev.notes
        }));
        toast.success("Dati estratti con successo dalla dettatura");
      }
    } catch (error) {
      toast.error("Errore nell'elaborazione della dettatura");
    }
  };

  const handleOCRResult = (parsed) => {
    // If client_id was parsed (from parse-pdf-complete), find and set the client
    if (parsed.client_id) {
      const client = clients.find(c => c.client_id === parsed.client_id);
      if (client) {
        setSelectedClient(client);
        setClientSearch(client.name);
        toast.info(`Cliente trovato: ${client.name}`);
      }
    } else if (parsed.client_name) {
      // Try to match by name (exact, contains, or parts)
      const parsedParts = parsed.client_name.toUpperCase().split(/\s+/).filter(p => p.length > 1);
      let client = null;
      
      // 1. Exact match
      client = clients.find(c => c.name.toUpperCase() === parsed.client_name.toUpperCase());
      
      // 2. Partial match (contains)
      if (!client) {
        const clientName = parsed.client_name.toUpperCase();
        client = clients.find(c => 
          c.name.toUpperCase().includes(clientName) || clientName.includes(c.name.toUpperCase())
        );
      }
      
      // 3. Parts match (handles "GRAZZINI GIANNI" vs "Gianni Grazzini")
      if (!client && parsedParts.length >= 2) {
        client = clients.find(c => {
          const cParts = c.name.toUpperCase().split(/\s+/).filter(p => p.length > 1);
          if (cParts.length < 2) return false;
          const matching = parsedParts.filter(pp => cParts.some(cp => cp === pp));
          return matching.length >= Math.min(parsedParts.length, cParts.length);
        });
      }
      
      // 4. Fiscal code match (if available in parsed data)
      if (!client && parsed.fiscal_code) {
        client = clients.find(c => 
          c.fiscal_code && c.fiscal_code.toUpperCase() === parsed.fiscal_code.toUpperCase()
        );
      }
      
      if (client) {
        setSelectedClient(client);
        setClientSearch(client.name);
        setFormData(prev => ({ ...prev, client_id: client.client_id }));
        toast.info(`Cliente trovato: ${client.name}`);
      } else {
        // Set the name in search so user can see what was extracted
        setClientSearch(parsed.client_name);
        toast.warning(`Cliente "${parsed.client_name}" non trovato - seleziona manualmente`);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      client_id: parsed.client_id || prev.client_id,
      policy_number: parsed.policy_number || prev.policy_number,
      company: parsed.company || prev.company,
      policy_type: parsed.policy_type || prev.policy_type,
      premium: parsed.premium?.toString() || prev.premium,
      consultation_fee: parsed.consultation_fee?.toString() || prev.consultation_fee,
      start_date: parsed.start_date || prev.start_date,
      expiry_date: parsed.expiry_date || prev.expiry_date,
      license_plate: parsed.license_plate || prev.license_plate,
      payment_frequency: parsed.payment_frequency || prev.payment_frequency,
      notes: parsed.notes || prev.notes
    }));
    setShowOCR(false);
  };

  // Client search functions
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (client.fiscal_code && client.fiscal_code.toLowerCase().includes(clientSearch.toLowerCase())) ||
    (client.email && client.email.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch(client.name);
    setFormData(prev => ({ ...prev, client_id: client.client_id }));
    setShowClientSuggestions(false);
  };

  const clearClientSelection = () => {
    setSelectedClient(null);
    setClientSearch("");
    setFormData(prev => ({ ...prev, client_id: "" }));
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client ? client.name : "N/A";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getStatusBadge = (policy) => {
    // Chiusa in anticipo (19/10/2026)
    if (policy.status === "cancelled") {
      return (
        <div className="flex flex-col gap-1">
          <span className="px-2 py-1 rounded-sm text-xs font-medium bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
            ⛔ Chiusa
          </span>
          {policy.closure_date && (
            <span className="text-xs text-muted-foreground">
              il {formatDate(policy.closure_date, "-")}{policy.closure_reason ? ` · ${policy.closure_reason}` : ""}
            </span>
          )}
        </div>
      );
    }

    // Suspended status
    if (policy.suspended) {
      return (
        <div className="flex flex-col gap-1">
          <span className="px-2 py-1 rounded-sm text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
            ⏸️ Sospesa
          </span>
          {policy.suspension_reminder && (
            <span className="text-xs text-muted-foreground">
              Reminder: {formatDate(policy.suspension_reminder, "-")}
            </span>
          )}
        </div>
      );
    }
    
    // Renewed status
    if (policy.renewed) {
      return (
        <div className="flex flex-col gap-1">
          <span className="px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            ✓ Rinnovata
          </span>
          {policy.renewed_with_company && (
            <span className="text-xs text-muted-foreground">
              → {policy.renewed_with_company}
            </span>
          )}
        </div>
      );
    }
    
    const colors = {
      active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      expired: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      cancelled: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
    };
    const labels = {
      active: "Attiva",
      expired: "Scaduta",
      cancelled: "Chiusa"
    };
    return (
      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${colors[policy.status] || colors.active}`}>
        {labels[policy.status] || policy.status}
      </span>
    );
  };

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (advancedFilters.company) count++;
    if (advancedFilters.policyTypes.length > 0) count++;
    if (advancedFilters.status) count++;
    if (advancedFilters.expiryDateFrom || advancedFilters.expiryDateTo) count++;
    if (advancedFilters.premiumMin || advancedFilters.premiumMax) count++;
    if (advancedFilters.licensePlate) count++;
    if (advancedFilters.paymentFrequency) count++;
    setActiveFiltersCount(count);
  }, [advancedFilters]);

  // Reset advanced filters
  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      company: "",
      policyTypes: [],
      status: "",
      expiryDateFrom: "",
      expiryDateTo: "",
      premiumMin: "",
      premiumMax: "",
      licensePlate: "",
      paymentFrequency: "",
    });
  };

  // Get unique companies from policies
  const uniqueCompanies = [...new Set(policies.map(p => p.company))].filter(Boolean).sort();

  // Get policy types sorted by frequency (most used first)
  const getSortedPolicyTypes = () => {
    // Count occurrences of each type
    const typeCounts = {};
    policies.forEach(p => {
      typeCounts[p.policy_type] = (typeCounts[p.policy_type] || 0) + 1;
    });
    
    // Separate into "most used" (count > 0) and "others"
    const usedTypes = POLICY_TYPES
      .filter(t => typeCounts[t.value] > 0)
      .sort((a, b) => (typeCounts[b.value] || 0) - (typeCounts[a.value] || 0));
    
    const unusedTypes = POLICY_TYPES
      .filter(t => !typeCounts[t.value]);
    
    return { usedTypes, unusedTypes, typeCounts };
  };
  
  const { usedTypes, unusedTypes, typeCounts } = getSortedPolicyTypes();

  // Filter policies - search is handled by backend, only apply tab and advanced filters locally
  const filteredPolicies = policies.filter(policy => {
    // Tab filter
    if (activeTab === "suspended") {
      if (policy.suspended !== true) return false;
    } else if (activeTab === "chiuse") {
      if (policy.status !== "cancelled") return false;
    } else if (activeTab === "storico") {
      // le polizze dello storico arrivano già filtrate dal backend
    } else {
      if (policy.suspended || policy.status === "cancelled") return false;
    }

    // Advanced filters
    if (advancedFilters.company && policy.company !== advancedFilters.company) return false;
    if (advancedFilters.policyTypes.length > 0 && !advancedFilters.policyTypes.includes(policy.policy_type)) return false;
    if (advancedFilters.status) {
      if (advancedFilters.status === "active" && (policy.status !== "active" || policy.renewed || policy.suspended)) return false;
      if (advancedFilters.status === "expired" && policy.status !== "expired") return false;
      if (advancedFilters.status === "suspended" && !policy.suspended) return false;
      if (advancedFilters.status === "renewed" && !policy.renewed) return false;
      if (advancedFilters.status === "cancelled" && policy.status !== "cancelled") return false;
    }
    if (advancedFilters.expiryDateFrom && policy.expiry_date < advancedFilters.expiryDateFrom) return false;
    if (advancedFilters.expiryDateTo && policy.expiry_date > advancedFilters.expiryDateTo) return false;
    if (advancedFilters.premiumMin && getPolicyPremiumTotal(policy) < parseFloat(advancedFilters.premiumMin)) return false;
    if (advancedFilters.premiumMax && getPolicyPremiumTotal(policy) > parseFloat(advancedFilters.premiumMax)) return false;
    if (advancedFilters.licensePlate && (!policy.license_plate || !policy.license_plate.toLowerCase().includes(advancedFilters.licensePlate.toLowerCase()))) return false;
    if (advancedFilters.paymentFrequency && policy.payment_frequency !== advancedFilters.paymentFrequency) return false;
    
    return true;
  });

  const suspendedCount = policies.filter(p => p.suspended).length;

  // Export filtered results
  const handleExportFiltered = async (format, columns, sortBy, sortDir, landscape) => {
    setExporting(true);
    try {
      const policyIds = filteredPolicies.map(p => p.policy_id);
      const body = { policy_ids: policyIds };
      if (columns) body.columns = columns;
      if (sortBy && sortBy !== "none") {
        body.sort_by = sortBy;
        body.sort_dir = sortDir || "asc";
      }
      if (landscape) body.landscape = true;
      
      const response = await axios.post(`/api/policies/export/${format}`, body, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format === 'pdf' ? 'pdf' : 'xlsx';
      link.setAttribute('download', `polizze_filtrate.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} completato`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error("Errore nell'esportazione");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="policies-page-loading" className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <Skeleton className="h-10 w-32 mb-2" />
            <Skeleton className="h-4 w-56" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Skeleton className="h-10 w-28" />
            <Skeleton className="h-10 w-28" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-5" />
                </div>
                <Skeleton className="h-8 w-12 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search and Filters Skeleton */}
        <div className="flex flex-col md:flex-row gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Table Skeleton */}
        <Card>
          <CardContent className="p-0">
            <div className="space-y-0">
              <div className="p-4 border-b">
                <div className="flex gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Skeleton key={i} className="h-4 w-24" />
                  ))}
                </div>
              </div>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="p-4 border-b">
                  <div className="flex gap-4 items-center">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-8" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="policies-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
            Polizze
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Gestisci le polizze assicurative
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Export button */}
          <Button variant="outline" onClick={() => setExportDialogOpen(true)} data-testid="export-btn">
            <Download className="mr-2 h-4 w-4" />
            Esporta ({filteredPolicies.length})
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-policy-btn" className="bg-primary hover:bg-primary/90">
                <Plus className="mr-2 h-4 w-4" />
                Nuova Polizza
              </Button>
            </DialogTrigger>
          <DialogContent aria-describedby={undefined} className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingPolicy ? "Modifica Polizza" : "Nuova Polizza"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                {/* Document Dropzone + Voice */}
                <div className="space-y-2">
                  <DocumentDropzone
                    type="policy"
                    onResult={handleOCRResult}
                    onClose={() => setShowOCR(false)}
                    onFileStored={(file) => { pendingFileRef.current = file; }}
                  />
                  <div className="flex items-center gap-2">
                    <VoiceInput onResult={handleVoiceResult} />
                    <span className="text-xs text-muted-foreground">
                      oppure dettatura vocale
                    </span>
                  </div>
                </div>

                {/* Client Search */}
                <div className="grid gap-2">
                  <Label htmlFor="client_search">Cliente *</Label>
                  <div className="relative" ref={clientSearchRef}>
                    {selectedClient ? (
                      <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary rounded-sm">
                        <User className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{selectedClient.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedClient.fiscal_code || selectedClient.email || selectedClient.phone || ""}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={clearClientSelection}
                          className="h-8 w-8"
                         aria-label="Chiudi">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="client_search"
                            data-testid="client-search-input"
                            value={clientSearch}
                            onChange={(e) => {
                              setClientSearch(e.target.value);
                              setShowClientSuggestions(true);
                            }}
                            onFocus={() => setShowClientSuggestions(true)}
                            placeholder="Cerca cliente per nome, codice fiscale o email..."
                            className="pl-10"
                          />
                        </div>
                        
                        {/* Suggestions dropdown */}
                        {showClientSuggestions && clientSearch && (
                          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-sm shadow-lg max-h-60 overflow-y-auto">
                            {filteredClients.length === 0 ? (
                              <div className="p-3 text-sm text-muted-foreground text-center">
                                Nessun cliente trovato
                              </div>
                            ) : (
                              filteredClients.slice(0, 10).map((client) => (
                                <button
                                  key={client.client_id}
                                  type="button"
                                  onClick={() => handleClientSelect(client)}
                                  className="w-full p-3 text-left hover:bg-muted transition-colors border-b border-border last:border-0"
                                >
                                  <p className="font-medium text-foreground">{client.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {[client.fiscal_code, client.email, client.phone].filter(Boolean).join(" • ")}
                                  </p>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="policy_number">Numero Polizza *</Label>
                    <Input
                      id="policy_number"
                      data-testid="policy-number-input"
                      value={formData.policy_number}
                      onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="company">Compagnia *</Label>
                    <div className="relative">
                      <Input
                        id="company"
                        data-testid="policy-company-input"
                        value={formData.company}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase();
                          setFormData({ ...formData, company: val });
                        }}
                        className="uppercase"
                        required
                        autoComplete="off"
                      />
                      {formData.company && formData.company.length >= 2 && (
                        (() => {
                          const suggestions = uniqueCompanies.filter(c => 
                            c.toUpperCase().includes(formData.company.toUpperCase()) && 
                            c.toUpperCase() !== formData.company.toUpperCase()
                          );
                          if (suggestions.length === 0) return null;
                          return (
                            <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {suggestions.slice(0, 5).map(company => (
                                <div
                                  key={company}
                                  className="px-3 py-2 hover:bg-accent cursor-pointer text-sm"
                                  onClick={() => setFormData({ ...formData, company })}
                                >
                                  {company}
                                </div>
                              ))}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="policy_type">Tipo Polizza *</Label>
                    <Select
                      value={formData.policy_type}
                      onValueChange={(value) => setFormData({ ...formData, policy_type: value })}
                    >
                      <SelectTrigger data-testid="policy-type-select">
                        <SelectValue placeholder="Seleziona tipo">
                          {formData.policy_type && (
                            <span className="flex items-center gap-2">
                              <span>{getPolicyTypeInfo(formData.policy_type).icon}</span>
                              <span>{getPolicyTypeInfo(formData.policy_type).label}</span>
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {usedTypes.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                              Più usati
                            </div>
                            {usedTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <span className="flex items-center gap-2">
                                  <span>{type.icon}</span>
                                  <span>{type.label}</span>
                                  <span className="text-xs text-muted-foreground ml-auto">({typeCounts[type.value]})</span>
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {unusedTypes.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                              {usedTypes.length > 0 ? "Altri tipi" : "Tipi polizza"}
                            </div>
                            {unusedTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <span className="flex items-center gap-2">
                                  <span>{type.icon}</span>
                                  <span>{type.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="premium">Premio di Polizza (€) *</Label>
                    <Input
                      id="premium"
                      type="number"
                      step="0.01"
                      data-testid="policy-premium-input"
                      value={formData.premium}
                      onChange={(e) => setFormData({ ...formData, premium: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="consultation_fee">Consulenza (€)</Label>
                    <Input
                      id="consultation_fee"
                      type="number"
                      step="0.01"
                      data-testid="policy-consultation-input"
                      value={formData.consultation_fee}
                      onChange={(e) => setFormData({ ...formData, consultation_fee: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Totale</Label>
                    <div className="h-10 px-3 py-2 bg-muted rounded-sm border border-input flex items-center font-semibold text-primary">
                      {formatCurrency((parseFloat(formData.premium) || 0) + (parseFloat(formData.consultation_fee) || 0))}
                    </div>
                    {formData.additional_services?.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Servizi inclusi nel premio: +{formatCurrency(formData.additional_services.reduce((s, svc) => s + (parseFloat(svc.cost) || 0), 0))}
                      </p>
                    )}
                  </div>
                </div>

                {/* Payment Frequency */}
                <div className="grid gap-2">
                  <Label>Frequenza Pagamento *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.payment_frequency === "annual" ? "default" : "outline"}
                      className={`flex-1 ${formData.payment_frequency === "annual" ? "bg-primary" : ""}`}
                      onClick={() => setFormData({ ...formData, payment_frequency: "annual" })}
                    >
                      Annuale
                    </Button>
                    <Button
                      type="button"
                      variant={formData.payment_frequency === "semiannual" ? "default" : "outline"}
                      className={`flex-1 ${formData.payment_frequency === "semiannual" ? "bg-primary" : ""}`}
                      onClick={() => setFormData({ ...formData, payment_frequency: "semiannual" })}
                    >
                      Semestrale
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="start_date">Data Inizio *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      data-testid="policy-start-date-input"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="expiry_date">Scadenza Annuale *</Label>
                    <Input
                      id="expiry_date"
                      type="date"
                      data-testid="policy-expiry-date-input"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Semiannual Expiry - only for semiannual payments */}
                {formData.payment_frequency === "semiannual" && (
                  <div className="grid gap-2">
                    <Label htmlFor="semiannual_expiry">Scadenza Rata Semestrale</Label>
                    <Input
                      id="semiannual_expiry"
                      type="date"
                      data-testid="policy-semiannual-expiry-input"
                      value={formData.semiannual_expiry}
                      onChange={(e) => setFormData({ ...formData, semiannual_expiry: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Calcolata automaticamente 6 mesi dalla data inizio. Puoi modificarla se necessario.
                    </p>
                  </div>
                )}

                {/* License Plate / Chassis - for vehicles */}
                {/* 29/09/2026: aggiunto "cvt" (Corpi Veicoli Terrestri) — ha la targa. */}
                {(formData.policy_type === "auto" || formData.policy_type === "moto" || formData.policy_type === "autocarri" || formData.policy_type === "cvt") && (
                  <div className="grid gap-2">
                    <Label htmlFor="license_plate">Targa Veicolo</Label>
                    <Input
                      id="license_plate"
                      data-testid="policy-license-plate-input"
                      placeholder="AB123CD"
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                    />
                  </div>
                )}

                {formData.policy_type === "altri_veicoli" && (
                  <div className="grid gap-2">
                    <Label htmlFor="license_plate">Targa / Telaio</Label>
                    <Input
                      id="license_plate"
                      data-testid="policy-license-plate-input"
                      placeholder="Targa o numero telaio"
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                    />
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="notes">Note</Label>
                  <Textarea
                    id="notes"
                    data-testid="policy-notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>

                {/* Servizi Aggiuntivi */}
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label>Servizi Aggiuntivi</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="add-service-btn"
                      onClick={() => {
                        const services = [...(formData.additional_services || [])];
                        services.push({ provider: "", description: "", cost: "" });
                        setFormData({ ...formData, additional_services: services });
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Aggiungi
                    </Button>
                  </div>
                  {(formData.additional_services || []).map((svc, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`service-row-${idx}`}>
                      <Input
                        placeholder="Fornitore"
                        value={svc.provider || ""}
                        onChange={(e) => {
                          const services = [...(formData.additional_services || [])];
                          services[idx] = { ...services[idx], provider: e.target.value };
                          setFormData({ ...formData, additional_services: services });
                        }}
                        className="flex-1"
                        data-testid={`service-provider-${idx}`}
                      />
                      <Input
                        placeholder="Descrizione"
                        value={svc.description || ""}
                        onChange={(e) => {
                          const services = [...(formData.additional_services || [])];
                          services[idx] = { ...services[idx], description: e.target.value };
                          setFormData({ ...formData, additional_services: services });
                        }}
                        className="flex-1"
                        data-testid={`service-description-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Costo €"
                        value={svc.cost || ""}
                        onChange={(e) => {
                          const services = [...(formData.additional_services || [])];
                          services[idx] = { ...services[idx], cost: e.target.value };
                          setFormData({ ...formData, additional_services: services });
                        }}
                        className="w-24"
                        data-testid={`service-cost-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        data-testid={`remove-service-${idx}`}
                        onClick={() => {
                          const services = [...(formData.additional_services || [])];
                          services.splice(idx, 1);
                          setFormData({ ...formData, additional_services: services });
                        }}
                       aria-label="Rimuovi il servizio">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  {(formData.additional_services || []).length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Totale servizi: {formatCurrency((formData.additional_services || []).reduce((s, svc) => s + (parseFloat(svc.cost) || 0), 0))}
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="save-policy-btn" className="bg-primary hover:bg-primary/90">
                  {editingPolicy ? "Aggiorna" : "Salva"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Advanced Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="search-policies-input"
            placeholder="Cerca per cliente, compagnia, targa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Sheet open={advancedFiltersOpen} onOpenChange={setAdvancedFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="relative">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filtri
              {activeFiltersCount > 0 && (
                <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs bg-primary">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[400px] sm:w-[450px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-heading flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filtri Avanzati
              </SheetTitle>
            </SheetHeader>
            
            <div className="space-y-6 py-6">
              {/* Company */}
              <div className="space-y-2">
                <Label>Compagnia</Label>
                <Select
                  value={advancedFilters.company}
                  onValueChange={(value) => setAdvancedFilters(prev => ({ ...prev, company: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tutte le compagnie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le compagnie</SelectItem>
                    {uniqueCompanies.map(company => (
                      <SelectItem key={company} value={company}>{company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Policy Types */}
              <div className="space-y-2">
                <Label>Tipo Polizza</Label>
                <div className="grid grid-cols-2 gap-2">
                  {POLICY_TYPES.map(type => (
                    <div key={type.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type.value}`}
                        checked={advancedFilters.policyTypes.includes(type.value)}
                        onCheckedChange={(checked) => {
                          setAdvancedFilters(prev => ({
                            ...prev,
                            policyTypes: checked
                              ? [...prev.policyTypes, type.value]
                              : prev.policyTypes.filter(t => t !== type.value)
                          }));
                        }}
                      />
                      <label htmlFor={`type-${type.value}`} className="text-sm cursor-pointer flex items-center gap-1">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select
                  value={advancedFilters.status}
                  onValueChange={(value) => setAdvancedFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tutti gli stati" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="active">Attiva</SelectItem>
                    <SelectItem value="expired">Scaduta</SelectItem>
                    <SelectItem value="suspended">Sospesa</SelectItem>
                    <SelectItem value="renewed">Rinnovata</SelectItem>
                    <SelectItem value="cancelled">Chiusa</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Expiry Date Range */}
              <div className="space-y-2">
                <Label>Range Scadenza</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Da</Label>
                    <Input
                      type="date"
                      value={advancedFilters.expiryDateFrom}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, expiryDateFrom: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">A</Label>
                    <Input
                      type="date"
                      value={advancedFilters.expiryDateTo}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, expiryDateTo: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Premium Range */}
              <div className="space-y-2">
                <Label>Range Premio (€)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.premiumMin}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, premiumMin: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="∞"
                      value={advancedFilters.premiumMax}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, premiumMax: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* License Plate */}
              <div className="space-y-2">
                <Label>Targa</Label>
                <Input
                  placeholder="Cerca targa..."
                  value={advancedFilters.licensePlate}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, licensePlate: e.target.value.toUpperCase() }))}
                />
              </div>

              {/* Payment Frequency */}
              <div className="space-y-2">
                <Label>Frequenza Pagamento</Label>
                <Select
                  value={advancedFilters.paymentFrequency}
                  onValueChange={(value) => setAdvancedFilters(prev => ({ ...prev, paymentFrequency: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tutte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="annual">Annuale</SelectItem>
                    <SelectItem value="semiannual">Semestrale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <SheetFooter className="flex gap-2">
              <Button variant="outline" onClick={resetAdvancedFilters} className="flex-1">
                <X className="h-4 w-4 mr-2" />
                Resetta
              </Button>
              <Button onClick={() => setAdvancedFiltersOpen(false)} className="flex-1">
                <CheckCircle className="h-4 w-4 mr-2" />
                Applica ({filteredPolicies.length})
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {/* Active filters summary */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>{activeFiltersCount} filtri attivi</span>
          <span>•</span>
          <span className="font-medium text-foreground">{filteredPolicies.length} risultati</span>
          <Button variant="ghost" size="sm" onClick={resetAdvancedFilters} className="h-6 px-2 text-xs">
            Resetta filtri
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-policies">
            Tutte ({policies.filter(p => !p.suspended && p.status !== "cancelled").length})
          </TabsTrigger>
          <TabsTrigger value="suspended" data-testid="tab-suspended-policies">
            <Pause className="h-4 w-4 mr-1" />
            Sospese ({suspendedCount})
          </TabsTrigger>
          <TabsTrigger value="chiuse" data-testid="tab-closed-policies">
            ⛔ Chiuse ({policies.filter(p => p.status === "cancelled").length})
          </TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico-policies">
            🕘 Storico ({storicoCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
      {/* Policies Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loadError && filteredPolicies.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
              <p className="font-medium text-foreground">Non riesco a caricare le polizze</p>
              <p className="text-sm text-muted-foreground">
                I dati non sono andati persi: e' il collegamento che non ha risposto.
              </p>
              <Button variant="outline" size="sm" onClick={() => fetchPolicies(currentPage, searchTerm)}>
                Riprova
              </Button>
            </div>
          ) : filteredPolicies.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {activeTab === "suspended" 
                  ? "Nessuna polizza sospesa" 
                  : (searchTerm ? "Nessuna polizza trovata" : "Nessuna polizza presente. Crea la prima!")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="Cliente" sortKey="client_name_sort" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Compagnia" sortKey="company" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Tipo" sortKey="policy_type" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label={activeTab === "suspended" ? "Sospesa dal" : "Scadenza"} sortKey="expiry_date" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Premio" sortKey="premium" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Consulenza" sortKey="consultation_fee" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                    </TableHead>
                    <TableHead>Totale</TableHead>
                    <TableHead>Targa</TableHead>
                    <TableHead>
                      <SortableHeader label="Stato" sortKey="status" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                    </TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortData(filteredPolicies.map(p => ({...p, client_name_sort: getClientName(p.client_id)})), tableSort.key, tableSort.dir).map((policy) => (
                    <TableRow 
                      key={policy.policy_id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/policies/${policy.policy_id}`)}
                      data-testid={`policy-row-${policy.policy_id}`}
                    >
                      <TableCell className="font-medium text-primary">{getClientName(policy.client_id)}</TableCell>
                      <TableCell>{policy.company}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <span>{getPolicyTypeInfo(policy.policy_type).icon}</span>
                          <span className="capitalize">{getPolicyTypeInfo(policy.policy_type).label}</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          {policy.suspended ? (
                            <>
                              {formatDate(policy.suspension_date, "-")}
                              {policy.suspension_reason && (
                                <div className="text-xs text-muted-foreground">"{policy.suspension_reason}"</div>
                              )}
                            </>
                          ) : (
                            <>
                              <div>{formatDate(policy.expiry_date, "-")}</div>
                              {policy.payment_frequency === "semiannual" && policy.semiannual_expiry && (
                                <div className="text-xs text-orange-600 font-medium">
                                  Sem: {formatDate(policy.semiannual_expiry, "-")}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatCurrency(getPolicyPremiumTotal(policy))}</TableCell>
                      <TableCell>{formatCurrency(policy.consultation_fee)}</TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(getPolicyGrandTotal(policy))}
                      </TableCell>
                      <TableCell>
                        {policy.license_plate ? (
                          <span className="font-mono text-sm">{policy.license_plate}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(policy)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {/* Suspended policy actions */}
                          {policy.suspended && (
                            <Button
                              variant="outline"
                              size="icon"
                              title="Riattiva polizza"
                              onClick={() => openReactivateDialog(policy)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {/* Active policy actions */}
                          {!policy.suspended && !policy.renewed && (policy.status === 'active' || !policy.status) && (
                            <>
                              {/* Suspend button - only for vehicle policies */}
                              {SUSPENDABLE_TYPES.includes(policy.policy_type) && (
                                <Button
                                  variant="outline"
                                  size="icon"
                                  title="Sospendi polizza"
                                  onClick={() => openSuspendDialog(policy)}
                                  className="text-orange-600 hover:text-orange-700"
                                >
                                  <Pause className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="icon"
                                title="Gestione Rinnovo"
                                onClick={() => openRenewalDialog(policy)}
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`renewal-dialog-${policy.policy_id}`}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {policy.renewed && (
                            <Button
                              variant="outline"
                              size="icon"
                              title="Rimuovi flag rinnovata"
                              onClick={() => handleUnmarkRenewed(policy)}
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                          {(policy.not_renewed || policy.status === "not_renewed") && (
                            <Button
                              variant="outline"
                              size="icon"
                              title='Annulla "non rinnovata" (ripristina la polizza)'
                              onClick={() => handleUndoNotRenewed(policy.policy_id)}
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="icon"
                            title="Documenti"
                            onClick={() => {
                              setSelectedPolicyForDocs(policy);
                              setDocumentsDialogOpen(true);
                            }}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            data-testid={`edit-policy-${policy.policy_id}`}
                            onClick={() => handleEdit(policy)}
                           aria-label="Modifica la polizza">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            data-testid={`delete-policy-${policy.policy_id}`}
                            onClick={() => confirmDeletePolicy(policy)}
                            className="text-destructive hover:text-destructive"
                           aria-label="Elimina la polizza">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Server-side Pagination - Desktop */}
          {totalPages > 1 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalPolicies}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
            />
          )}
          
          {/* Mobile Cards */}
          <div className="md:hidden space-y-4 p-4">
            {loadError && filteredPolicies.length === 0 ? (
              <div className="text-center py-8 space-y-3">
                <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
                <p className="font-medium text-foreground">Non riesco a caricare le polizze</p>
                <p className="text-sm text-muted-foreground">
                  I dati non sono andati persi: e' il collegamento che non ha risposto.
                </p>
                <Button variant="outline" size="sm" onClick={() => fetchPolicies(currentPage, searchTerm)}>
                  Riprova
                </Button>
              </div>
            ) : filteredPolicies.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {searchTerm ? "Nessuna polizza trovata" : "Nessuna polizza presente. Crea la prima!"}
              </p>
            ) : (
              filteredPolicies.map((policy) => (
                <div 
                  key={policy.policy_id} 
                  className="bg-muted/30 rounded-lg p-4 border border-border cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
                  onClick={() => navigate(`/policies/${policy.policy_id}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-primary">{getClientName(policy.client_id)}</h3>
                      <p className="text-sm text-muted-foreground">{policy.company}</p>
                    </div>
                    {getStatusBadge(policy)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <span className="text-muted-foreground">Tipo: </span>
                      <span className="text-foreground flex items-center gap-1 inline-flex">
                        <span>{getPolicyTypeInfo(policy.policy_type).icon}</span>
                        <span>{getPolicyTypeInfo(policy.policy_type).label}</span>
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {policy.suspended ? "Sospesa dal: " : "Scadenza: "}
                      </span>
                      <span className="text-foreground">
                        {policy.suspended ? (
                          formatDate(policy.suspension_date, "-")
                        ) : (
                          formatDate(policy.expiry_date, "-")
                        )}
                      </span>
                      {!policy.suspended && policy.payment_frequency === "semiannual" && policy.semiannual_expiry && (
                        <span className="text-xs text-orange-600 font-medium block">
                          Sem: {formatDate(policy.semiannual_expiry, "-")}
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Premio: </span>
                      <span className="text-foreground">{formatCurrency(getPolicyPremiumTotal(policy))}</span>
                      {getServicesTotal(policy) > 0 && (
                        <span className="text-xs text-muted-foreground ml-1">(incl. servizi)</span>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Consulenza: </span>
                      <span className="text-foreground">{formatCurrency(policy.consultation_fee)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Totale: </span>
                      <span className="text-foreground font-semibold">
                        {formatCurrency(getPolicyGrandTotal(policy))}
                      </span>
                    </div>
                    {policy.license_plate && (
                      <div>
                        <span className="text-muted-foreground">Targa: </span>
                        <span className="text-foreground font-mono">{policy.license_plate}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-1 pt-2 border-t border-border flex-wrap" onClick={(e) => e.stopPropagation()}>
                    {/* Suspended policy */}
                    {policy.suspended && (
                      <Button variant="outline" size="sm" onClick={() => openReactivateDialog(policy)}>
                        <Play className="h-4 w-4 mr-1" /> Riattiva
                      </Button>
                    )}
                    
                    {/* Active policy */}
                    {!policy.suspended && !policy.renewed && (policy.status === 'active' || !policy.status) && (
                      <>
                        {SUSPENDABLE_TYPES.includes(policy.policy_type) && (
                          <Button variant="outline" size="sm" onClick={() => openSuspendDialog(policy)}>
                            <Pause className="h-4 w-4 mr-1" /> Sospendi
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openRenewalDialog(policy)}>
                          <RefreshCw className="h-4 w-4 mr-1" /> Gestisci Rinnovo
                        </Button>
                      </>
                    )}
                    {policy.renewed && (
                      <Button variant="outline" size="sm" onClick={() => handleUnmarkRenewed(policy)}>
                        <RotateCcw className="h-4 w-4 mr-1" /> Annulla
                      </Button>
                    )}
                    <Button variant="outline" size="icon" onClick={() => { setSelectedPolicyForDocs(policy); setDocumentsDialogOpen(true); }} aria-label="Documenti della polizza">
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => handleEdit(policy)} aria-label="Modifica la polizza">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => confirmDeletePolicy(policy)} className="text-destructive" aria-label="Elimina la polizza">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
            
            {/* Server-side Pagination - Mobile */}
            {totalPages > 1 && (
              <DataPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalPolicies}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </CardContent>
      </Card>
      </TabsContent>
      </Tabs>

      {/* Suspend Policy Dialog - Using component */}
      <SuspendPolicyDialog
        open={suspendDialogOpen}
        onOpenChange={setSuspendDialogOpen}
        policy={selectedPolicyForAction}
        getClientName={getClientName}
        suspensionData={suspensionData}
        setSuspensionData={setSuspensionData}
        onSuspend={handleSuspend}
      />

      {/* Reactivate Policy Dialog - Using component */}
      <ReactivatePolicyDialog
        open={reactivateDialogOpen}
        onOpenChange={setReactivateDialogOpen}
        policy={selectedPolicyForAction}
        getClientName={getClientName}
        reactivationData={reactivationData}
        setReactivationData={setReactivationData}
        onReactivate={handleReactivate}
      />

      {/* Renewal Dialog - Using component */}
      <RenewalPolicyDialog
        open={renewalDialogOpen}
        onOpenChange={setRenewalDialogOpen}
        policy={selectedPolicyForRenewal}
        getClientName={getClientName}
        renewalData={renewalData}
        setRenewalData={setRenewalData}
        onRenew={handleRenewal}
        onUndo={() => selectedPolicyForRenewal && handleUndoSemester(selectedPolicyForRenewal.policy_id)}
        undoing={undoingSemester}
        saving={saving}
        renewalResult={renewalResult}
        uploading={uploadingAttach}
        onAttachDocument={allegaDocumentoRinnovo}
        onSkipAttach={saltaAllegato}
      />

      {/* Documents Dialog */}
      {selectedPolicyForDocs && (
        <DocumentsDialog
          open={documentsDialogOpen}
          onOpenChange={setDocumentsDialogOpen}
          entityType="policy"
          entityId={selectedPolicyForDocs.policy_id}
          entityName={`Polizza ${selectedPolicyForDocs.policy_number}`}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        itemName={policyToDelete ? `Polizza ${policyToDelete.policy_number}` : "questa polizza"}
        onConfirm={handleDelete}
        loading={deleting}
        description={
          `Stai per eliminare definitivamente ${policyToDelete ? `la polizza ${policyToDelete.policy_number}` : "questa polizza"}: ` +
          `spariscono rate, storico incassi, note e conguagli, e i documenti collegati non saranno più raggiungibili.`
        }
        requireText="ELIMINA"
      />
      <ExportColumnsDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        exportType="policies"
        onExport={handleExportFiltered}
        exporting={exporting}
      />
    </div>
  );
}
